import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playAlertChime, chimesEnabled } from "@/components/SignalChimes";

const REASON = {
  regime_flip_swing_break: "Regime flip + swing break", tp_rupees: "₹ take-profit", risk_stop_rupees: "₹ max-risk stop", cutoff: "Time cutoff",
  kill_switch: "Kill switch", sl_hit: "Stop-loss hit", target_50_hit: "Target booked", runner_target_hit: "Runner target", counter_target_hit: "Counter target",
  counter_stop: "Counter stop", cutoff_1pm: "Hard square-off", breakeven_stop: "Breakeven stop", manual_squareoff: "Manual square-off", closed_externally: "Closed from terminal",
};
const inr = (n) => `₹${Math.round(Math.abs(n || 0)).toLocaleString("en-IN")}`;
const FRESH_MS = 20 * 60 * 1000;
const seenKey = "tiq_exit_seen";
const tokenKey = "tiq_token_alerted";

/** Global watcher (mounted once for Pro/Admin): chime + toast the moment any engine books an exit,
 *  and warn when the user's DHAN token is about to expire (≤ 60 min) or has expired. */
export default function EngineAlerts() {
  const { user } = useAuth();
  const seen = useRef(new Set(JSON.parse(localStorage.getItem(seenKey) || "[]")));
  const primed = useRef(false);
  const eligible = !!user && (user.is_admin || user.plan === "pro");

  useEffect(() => {
    if (!eligible) return;
    const poll = async () => {
      try {
        const { data } = await api.get("/alerts/exits?minutes=45");
        const fresh = (data.exits || []).filter((x) => x.exit_time && Date.now() - Date.parse(x.exit_time) < FRESH_MS);
        if (!primed.current) { fresh.forEach((x) => seen.current.add(x.trade_id)); primed.current = true; }
        fresh.filter((x) => !seen.current.has(x.trade_id)).reverse().forEach((x) => {
          seen.current.add(x.trade_id);
          const win = (x.pnl || 0) >= 0;
          if (chimesEnabled()) playAlertChime(win ? "win" : "loss");
          const title = `${x.engine} EXIT · ${x.symbol} ${x.strike || ""} ${x.opt_type || ""} ${win ? "+" : "−"}${inr(x.pnl)}`;
          (win ? toast.success : toast.error)(title, {
            description: `${REASON[x.exit_reason] || x.exit_reason || "exit"} · ${x.entry_premium ?? "—"} → ${x.exit_premium ?? "—"} · ${(x.mode || "").toUpperCase()}`,
            duration: 12000, id: `exit-${x.trade_id}`,
          });
        });
        localStorage.setItem(seenKey, JSON.stringify([...seen.current].slice(-200)));
      } catch (e) { console.warn("exit alerts poll failed:", e?.message || e); }
    };
    poll();
    const id = setInterval(poll, 6000);
    return () => clearInterval(id);
  }, [eligible]);

  useEffect(() => {
    if (!eligible) return;
    const check = async () => {
      try {
        const { data } = await api.get("/dhan/status");
        const h = data?.token_hours_left;
        if (h == null || !data.token_exp_iso) return;
        const stage = data.stale_token || h <= 0 ? "expired" : h <= 1 ? "1h" : null;
        if (!stage) return;
        const marker = `${data.token_exp_iso}:${stage}`;
        if (localStorage.getItem(tokenKey) === marker) return;
        localStorage.setItem(tokenKey, marker);
        if (chimesEnabled()) playAlertChime("token");
        const expAt = new Date(data.token_exp_iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
        (stage === "expired" ? toast.error : toast.warning)(
          stage === "expired" ? "DHAN token EXPIRED — auto-trade & execution paused" : `DHAN token expires in ${Math.max(1, Math.round(h * 60))} min (${expAt} IST)`,
          { description: "Generate a fresh token in DHAN (My Profile → DhanHQ APIs) and update it via the broker strip on the Dashboard.", duration: 30000, id: `token-${stage}` },
        );
      } catch (e) { console.warn("token check failed:", e?.message || e); }
    };
    check();
    const id = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [eligible]);

  return null;
}
